import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useConfig } from '../../../context/ConfigContext';
import { Restaurant } from '../../../interfaces/Catalogue';
import { RootState } from '../../../store/rootState';
import RestaurantSelectionModal from '../../common/RestaurantSelectionModal/RestaurantSelectionModal';
import TopBar from '../AuthSection/TopBar';
import './HeroSection.scss';

interface HeroSectionProps {
  restaurant?: Restaurant;
  restaurants?: Restaurant[];
  onSelectRestaurant?: (restaurant: Restaurant) => void;
  onLogout?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  restaurant,
  restaurants = [],
  onSelectRestaurant,
  onLogout,
}) => {
  const { config } = useConfig();
  const primaryColor = config?.primaryColor || '#6366F1';
  // const favoriteRestaurantId = useSelector((state: RootState) => state.app.favoriteRestaurantId);
  const [modalVisible, setModalVisible] = useState(restaurants.length > 1 
    // && !favoriteRestaurantId
  );

  const handleSelect = (restaurant: Restaurant) => {
    setModalVisible(false);
    onSelectRestaurant?.(restaurant);
  };

  // Check if we should show the switch button
  const shouldShowSwitchButton: boolean = restaurants.length > 1 && !restaurant;
  const shouldShowChangeButton: boolean = restaurants.length > 1 && !!restaurant;

  const handleSwitchPress = () => {
    setModalVisible(true);
  };

  return (
    <div className="hero-container">
      <img
        src={
          config?.backgroundMobileImage ||
          'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
        }
        className="hero-background"
        alt="Hero background"
      />

      <div className="hero-gradient" />
      <div
        className="brand-accent"
        style={{
          background: `linear-gradient(to bottom, transparent, ${primaryColor}10)`
        }}
      />

      <div className="hero-content">
        <div className="main-content">
          {/* Logo Section */}
          <div className="logo-container">
            <div className="compact-logo">
              <img
                src={config?.appLightLogoImage || config?.appLogoImage}
                className="logo-image"
                alt="Logo"
              />
            </div>
          </div>

          {/* Restaurant Info or Selection Prompt */}
          {restaurant ? (
            <div className="restaurant-info">
              <div className="welcome-text">Bienvenue chez</div>
              <div className="restaurant-title">
                {restaurant.nom}
              </div>
              {(restaurant.address || restaurant.phoneNumber) && (
                <div className="contact-text"> 📍 {restaurant.address}  {
                  restaurant.phoneNumber ? `- 📞 ${restaurant.phoneNumber}` : ""
                }
                </div>
              )}

              <div className="stats-row">
                <div className={`status-chip ${restaurant.isOpen ? 'open' : 'closed'}`}>
                  <div className={`status-indicator ${restaurant.isOpen ? 'open' : 'closed'}`} />
                  <span className={`status-text ${restaurant.isOpen ? 'open' : 'closed'}`}>
                    {restaurant.isOpen ? 'Ouvert' : 'Fermé'}
                  </span>
                </div>

                {restaurant.note > 0 && (
                  <div className="stat-chip">
                    <span className="stat-icon">⭐</span>
                    <span className="stat-text">{restaurant.note.toFixed(1)}</span>
                  </div>
                )}

                {restaurant.tempsPrep > 0 && (
                  <div className="stat-chip">
                    <span className="stat-icon">⏱</span>
                    <span className="stat-text">{restaurant.tempsPrep}min</span>
                  </div>
                )}

                {restaurant.isFavorite && (
                  <div className="stat-chip favorite-chip">
                    <span className="favorite-icon">❤️</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Show selection prompt when no restaurant is selected
            <div className="restaurant-info">
              <div className="welcome-text">Découvrez nos</div>
              <div className="restaurant-title">Restaurants</div>
              {restaurants.length > 0 && (
                <div
                  className="select-prompt"
                  onClick={handleSwitchPress}
                >
                  <div className="select-prompt-text">
                    Appuyez pour choisir ({restaurants?.length} disponibles)
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Top Bar with Auth Section and Switch Button */}
        <div className="top-bar">
          {/* Auth Section - Top Right */}
          <TopBar
            onLogout={onLogout}
            restaurant={restaurant}
            showSwitchButton={shouldShowSwitchButton || shouldShowChangeButton || false}
            onSwitchPress={handleSwitchPress}
          />

        </div>
      </div>

      {/* Restaurant Selection Modal */}
      <RestaurantSelectionModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSelect={handleSelect}
      />
    </div>
  );
}

export default HeroSection;